import React, { useState, useRef, useEffect } from 'react';
import Svg from 'components/utils/Svg/Svg';
import { imageLinks } from 'assets/images';
import WaveformVisualizer from '../../ChatWidgetInput/VoiceRecordingInterface/WaveformVisualizer/WaveformVisualizer';
import './VoiceNote.scss';

// Normalise duration coming from messages / recorder.
// Recorder gives milliseconds; audio elements/reporting use seconds.
const normaliseDurationProp = (value?: number): number => {
    if (!value || value <= 0) return 0;
    // Heuristic: values greater than 1000 are almost certainly milliseconds.
    return value > 1000 ? value / 1000 : value;
};

interface VoiceNoteProps {
    audioSource: string | Blob;
    uploadState?: string;
    errorMessage?: string;
    duration?: number;
    onRetry?: () => void;
    pausePlaybackSignal?: number;
}

const VoiceNote: React.FC<VoiceNoteProps> = ({ 
    audioSource, 
    uploadState,
    errorMessage,
    duration: initialDuration,
    onRetry,
    pausePlaybackSignal = 0
}) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(() => normaliseDurationProp(initialDuration));
    const [currentTime, setCurrentTime] = useState(0);
    const [audioUrl, setAudioUrl] = useState<string>('');
    const audioRef = useRef<HTMLAudioElement>(null);

    useEffect(() => {
        let objectUrl: string | null = null;
        const controller = new AbortController();

        const setFromBlob = (blob: Blob) => {
            objectUrl = URL.createObjectURL(blob);
            setAudioUrl(objectUrl);
        };

        const loadAudioSource = async () => {
            try {
                if (audioSource instanceof Blob) {
                    setFromBlob(audioSource);
                    return;
                }

                const response = await fetch(audioSource, { signal: controller.signal });
                if (!response.ok) {
                    throw new Error(`Failed to fetch audio: ${response.status}`);
                }
                const blob = await response.blob();
                setFromBlob(blob);
            } catch (error) {
                if ((error as { name?: string })?.name !== 'AbortError') {
                    // Fallback to direct URL for external sources without CORS
                    if (typeof audioSource === 'string') {
                        setAudioUrl(audioSource);
                    } else {
                        console.error('Error loading audio source:', error);
                    }
                }
            }
        };

        loadAudioSource();

        return () => {
            controller.abort();
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [audioSource]);

    // Update duration when initialDuration prop changes
    useEffect(() => {
        const normalised = normaliseDurationProp(initialDuration);
        if (normalised > 0 && duration === 0) {
            setDuration(normalised);
        }
    }, [initialDuration, duration]);

    useEffect(() => {
        if (!audioUrl) return;

        const audio = audioRef.current;
        if (!audio) return;

        // Explicitly set playback rate to 1 to prevent speed issues
        audio.playbackRate = 1;

        const handleTimeUpdate = () => {
            // Ensure we're getting accurate current time
            const currentTimeValue = audio.currentTime || 0;
            setCurrentTime(currentTimeValue);
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };

        const handleLoadedMetadata = () => {
            // Ensure playback rate is set when metadata is loaded
            audio.playbackRate = 1;
            // Use audio element duration if available and valid, otherwise fallback to initialDuration prop
            const propDurationSeconds = normaliseDurationProp(initialDuration);
            const audioDuration = Number.isFinite(audio.duration) && audio.duration > 0 
                ? audio.duration 
                : propDurationSeconds;
            if (audioDuration > 0) {
                setDuration(audioDuration);
            }
        };

        const handleCanPlay = () => {
            // Ensure playback rate is set when audio can play
            audio.playbackRate = 1;
        };

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('canplay', handleCanPlay);

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('canplay', handleCanPlay);
        };
    }, [audioUrl, initialDuration]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!pausePlaybackSignal || !audio || audio.paused) {
            return;
        }

        audio.pause();
        setIsPlaying(false);
    }, [pausePlaybackSignal]);

    useEffect(() => {
        const audio = audioRef.current;

        return () => {
            if (!audio) {
                return;
            }

            audio.pause();
            audio.removeAttribute('src');
            audio.load();
        };
    }, []);

    const handlePlayPause = () => {
        if (audioRef.current) {
            // Explicitly set playback rate before playing
            audioRef.current.playbackRate = 1;
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleSeek = (progress: number) => {
        if (audioRef.current && duration > 0) {
            const newTime = progress * duration;
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    const formatDuration = (timeInSeconds: number): string => {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = Math.floor(timeInSeconds % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    // Render upload state indicators as an overlay
    const renderUploadState = () => {
        if (uploadState === 'uploading') {
            return (
                <div></div>
            );
        } else if (uploadState === 'failed') {
            return (
                <div className="retry-icon-container">
                    <button className="retry-icon" onClick={onRetry} aria-label="Retry upload">
                        <Svg src={imageLinks.icon.error || ''} />
                    </button>
                </div>
            );
        }
        return null;
    };

    return (
        <div className={`voice-note ${uploadState ? `state-${uploadState}` : ''}`}>
            <div className="voice-note-content">
                {audioUrl && (
                    <>
                        <audio
                            ref={audioRef}
                            src={audioUrl}
                            preload="metadata"
                            style={{ display: 'none' }}
                        />
                        <button
                            className="playback-button"
                            onClick={handlePlayPause}
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                            disabled={uploadState === 'uploading'}
                        >
                            <Svg src={isPlaying ? imageLinks.icon.pbPause : imageLinks.icon.playIcon} />
                        </button>
                        <div className="wave--form-group">
                            <WaveformVisualizer
                                audioUrl={audioUrl}
                                isPlaying={isPlaying}
                                onPlayPause={handlePlayPause}
                                onSeek={handleSeek}
                                duration={duration || 0}
                                formattedDuration={formatDuration(duration || 0)}
                                currentProgress={duration > 0 ? Math.min(currentTime / duration, 1) : 0}
                            />
                            <span className="duration-text">
                                {!isPlaying && currentTime === 0
                                    ? formatDuration(duration)
                                    : `${formatDuration(currentTime)}`
                                }
                            </span>
                        </div>
                    </>
                )}
            </div>
            {renderUploadState()}
        </div>
    );
};

export default VoiceNote;
